import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Space, Spin, Typography } from 'antd'
import { filter, isEmpty, isNil, map } from 'lodash'

import { UpdateDataText } from '../../../core/components/sections'

import {
  FormButtons,
  HistoryLink,
  MetadataTreeErrorsModal,
  ReduxMetadataTree,
} from '../../../common/components/sections'
import {
  StyledPageActions,
  StyledPageHeader,
  StyledPageTitle,
  StyledVerticalSpace,
} from '../../../common/components/styled'
import {
  EVENT_METADATA_TYPES,
  METADATA_TYPES,
  PERMISSIONS,
  ROOT_METADATA_NODE_ID,
} from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import {
  useDocumentTitle,
  useLatestEventByEntityTypesQuery,
  useUserHasPermissions,
} from '../../../common/hooks'

import { DIET_NODE_TYPES } from '../../constants'
import { renderDietEvent } from '../../helpers'
import {
  useDeleteDietCategoryMutation,
  useDeleteDietMutation,
  useDietsAndDietCategoriesQuery,
  useSaveDietsAndDietCategoriesMutation,
  useSetIsCurationEnabledDietMutation,
} from '../../hooks'
import {
  addDietOrDietCategoryNode,
  expandDietAndDietCategoryMainTreeKeys,
  expandDietAndDietCategorySupportingTreeKeys,
  moveDiets,
  renameDietOrDietCategoryNode,
  resetDietsAndDietCategoriesTree,
  setDataForRenamingDietOrDietCategory,
} from '../../redux/actions'

const DietsPage = () => {
  const dispatch = useDispatch()
  useDocumentTitle('Diets')
  const [isMoveActive, setIsMoveActive] = useState(false)
  const [isReplaceActive, setIsReplaceActive] = useState(false)

  const eventEntityTypes = useMemo(
    () => [EVENT_METADATA_TYPES.DIET_CATEGORY, EVENT_METADATA_TYPES.DIET],
    [],
  )

  const {
    isLoading: isFetchLatestEventLoading,
    error: fetchLatestEventError,
    data: latestEvent,
    refetch: refetchLastEvent,
  } = useLatestEventByEntityTypesQuery(eventEntityTypes)

  const {
    isLoading: isFetchLoading,
    error: fetchError,
    initialData,
  } = useDietsAndDietCategoriesQuery()

  const [
    saveDietAndDietCategories,
    { isLoading: isSaveLoading, error: saveError },
  ] = useSaveDietsAndDietCategoriesMutation(refetchLastEvent)

  const [deleteDiet] = useDeleteDietMutation(refetchLastEvent)
  const [deleteDietCategory] = useDeleteDietCategoryMutation(refetchLastEvent)

  const [setIsCurationEnabledDiet] =
    useSetIsCurationEnabledDietMutation(refetchLastEvent)

  const {
    isDirty,
    data,
    dataForRenaming,
    mainTreeExpandedKeys,
    supportingTreeExpandedKeys,
  } = useSelector(state => state.diets.dietsAndDietCategories)

  const hasFullPermission = useUserHasPermissions([
    PERMISSIONS.METADATA_TREE.SAVE,
  ])

  const hasPermissionToViewDetails = useUserHasPermissions(
    PERMISSIONS.METADATA_TREE.GET,
  )

  const treeData = isDirty ? data : initialData

  const handleAdd = useCallback(
    node => {
      const isDietCategory = isNil(node.parentId)

      const nodeToCreate = isDietCategory
        ? {
            ...node,
            allowedOperations: METADATA_TYPES.DIET_CATEGORIES.allowedOperations,
          }
        : {
            ...node,
            allowedOperations: METADATA_TYPES.DIETS.allowedOperations,
          }

      dispatch(addDietOrDietCategoryNode(nodeToCreate))
    },
    [dispatch],
  )

  const handleDelete = useCallback(
    ({ id, type }) =>
      type === DIET_NODE_TYPES.DIET ? deleteDiet(id) : deleteDietCategory(id),
    [deleteDiet, deleteDietCategory],
  )

  const handleSetIsCurationEnabled = useCallback(
    ({ id, isCurationEnabled, type }) =>
      setIsCurationEnabledDiet(id, isCurationEnabled, type),
    [setIsCurationEnabledDiet],
  )

  const handleRename = useCallback(
    ({ path, name }) => dispatch(renameDietOrDietCategoryNode({ path, name })),
    [dispatch],
  )

  const handleSetDataForRenaming = useCallback(
    ({ id, type }) =>
      dispatch(setDataForRenamingDietOrDietCategory({ id, type })),
    [dispatch],
  )

  const handleMove = useCallback(
    (nodesToMove, destinationNodes) => {
      const [destinationNode] = destinationNodes

      if (destinationNode.type === DIET_NODE_TYPES.DIET) {
        return showErrorNotification({
          title: 'Move Error',
          description: <div>Can&apos;t move to a diet</div>,
        })
      }

      const selectedDietCategoryNodes = filter(nodesToMove, ({ parentId }) =>
        isNil(parentId),
      )

      if (!isEmpty(selectedDietCategoryNodes)) {
        return showErrorNotification({
          title: 'Move Error',
          description: (
            <div>
              Can&apos;t move diet categories:
              {map(selectedDietCategoryNodes, ({ name }) => (
                <Typography.Paragraph style={{ margin: 0 }} key={name}>
                  {name};
                </Typography.Paragraph>
              ))}
            </div>
          ),
        })
      }

      if (destinationNode.id === ROOT_METADATA_NODE_ID) {
        return showErrorNotification({
          title: 'Move Error',
          description: "Can't move diets to root",
        })
      }

      return dispatch(
        moveDiets({
          nodesToMove,
          destinationNode,
        }),
      )
    },
    [dispatch],
  )

  const handleResetTree = useCallback(
    () => dispatch(resetDietsAndDietCategoriesTree()),
    [dispatch],
  )

  const handleSaveData = useCallback(
    () => saveDietAndDietCategories(data),
    [data, saveDietAndDietCategories],
  )

  const showRenameInput = useCallback(
    (dataForRenaming, node) =>
      dataForRenaming?.id === node.id && dataForRenaming?.type === node.type,
    [],
  )

  const shouldPickSelectedSupportingItem = useCallback(
    (
      { id: mainNodeId, type: mainNodeType },
      { id: supportingNodeId, type: supportingNodeType },
    ) => mainNodeId === supportingNodeId && mainNodeType === supportingNodeType,
    [],
  )

  useEffect(() => {
    if (saveError?.message || fetchError?.message || fetchLatestEventError) {
      showErrorNotification({
        title: 'Diet / Diet category error',
        description:
          saveError?.message || fetchError?.message || fetchLatestEventError,
      })
    }
  }, [fetchError, fetchLatestEventError, saveError])

  const { entities } = saveError || {}

  return isFetchLoading ||
    isSaveLoading ||
    isFetchLatestEventLoading ||
    isEmpty(treeData) ? (
    <Spin />
  ) : (
    <>
      <StyledPageHeader>
        <StyledPageTitle>Diets</StyledPageTitle>
        <StyledPageActions>
          <StyledVerticalSpace>
            <Space>
              <UpdateDataText
                updatedAt={latestEvent?.createdAt}
                updatedBy={latestEvent?.createdBy?.name}
              />

              <HistoryLink
                renderEvent={renderDietEvent}
                filterEventTypes
                eventMetadataTypes={eventEntityTypes}
              />
            </Space>
            <FormButtons
              permissions={[PERMISSIONS.METADATA_TREE.SAVE]}
              isDisabled={isMoveActive || isReplaceActive ? true : !isDirty}
              onReset={handleResetTree}
              onSave={handleSaveData}
            />
          </StyledVerticalSpace>
        </StyledPageActions>
      </StyledPageHeader>
      <StyledVerticalSpace>
        {!isEmpty(entities) && <MetadataTreeErrorsModal errors={entities} />}
        <ReduxMetadataTree
          disableLink={!hasPermissionToViewDetails}
          mainTreeExpandedKeys={mainTreeExpandedKeys}
          supportingTreeExpandedKeys={supportingTreeExpandedKeys}
          expandMainTree={expandDietAndDietCategoryMainTreeKeys}
          expandSupportingTree={expandDietAndDietCategorySupportingTreeKeys}
          metadataType={METADATA_TYPES.DIET_CATEGORIES}
          secondaryMetadataType={METADATA_TYPES.DIETS}
          isDirty={isDirty}
          showSearch
          treeData={treeData}
          editable={hasFullPermission}
          addNode={handleAdd}
          deleteNode={handleDelete}
          renameNode={handleRename}
          moveNodes={handleMove}
          setDataForRenaming={handleSetDataForRenaming}
          setIsCurationEnabled={handleSetIsCurationEnabled}
          dataForRenaming={dataForRenaming}
          showRenameInput={showRenameInput}
          shouldPickSelectedSupportingItem={shouldPickSelectedSupportingItem}
          isMoveActive={isMoveActive}
          setIsMoveActive={setIsMoveActive}
          isReplaceActive={isReplaceActive}
          setIsReplaceActive={setIsReplaceActive}
        />
      </StyledVerticalSpace>
    </>
  )
}

export default DietsPage
